import {
  Box,
  Button,
  Grid,
  LinearProgress,
  Menu,
  MenuItem,
  styled
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getTableColumnData } from 'src/components/collapsible-table/table-column-prep';
import { getDataForSimulatedRows, getDataForSimulatedRowsWithBaseline } from 'src/components/collapsible-table/table-data-prep';
import {
  IFilterObj,
  getDefaultOptionsForFormFields
} from 'src/components/filters';
import { transformDynamicFormValueToFilterPayload } from 'src/components/filters/utils/filter-form-utils';
import { useAppDispatch, useAppSelector, useMenu } from 'src/hooks';
import useMatomoFunction from 'src/hooks/use-matomo-function.hook';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
// adding App Insights hook import for telemetry logging
import { useAppInsights } from 'src/hooks/useAppInsights';
import {
  FiltersWrapper,
  setGlobalFilterFieldOptions,
  setGlobalFilterValues
} from '../common/features';
import getOutputTableColumn from '../common/features/table/components/output-column-prep';
import {
  handlePhRecommendationCheckboxValueInputChange,
  setTableMetaData
} from '../common/features/table/store/table-slice';
import useModulePreRequisites from '../common/hooks/use-module-pre-requisites.hook';
import SaveAsContainer from '../components/save-as-container/save-as-container';
import SimulatorFooter from '../components/simulation-footer/simulator-footer';
import { MatomoEventTrackMapObject } from '../constants/constant';
import ScenarioCreateSuccessDialog from '../home/components/scenario-create-success/scenario-create-success-dialog';
import MODULE_TO_PAGE_MAP from '../home/types/simulation-module-to-page-map';
import DrawerBoxContainer from '../pricing-simulator/components/comment-box-container/drawer-box-container';
import TableSkeletonContainer from '../pricing-simulator/components/simulation-table-container/table-skeleton';
import {
  InputType,
  MODIFIED_ROW_INDICATOR_KEY,
  TableComponent
} from '../pricing-simulator/types/generic-table.model';
import DelistSkuInfo from './components/delist-sku-info/delist-sku-info';
import DelistTableComponent from './components/delist-table-component/delist-table';
import './index.scss';
import {
  getSKUDelistData,
  handleDelistSimulationFlagValue,
  handleValueOfDelistSavedScenario,
  resetDelistSimulationData,
  saveDelistSimulationData
} from './store/sku-delist-slice';
import OnNextClickHandler from '../common/utils/simulator-page-next-button';
import { AlertDialog, DialogResult } from '../../../../../src/components/analytics/alert-dialog/index';



const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: theme.spacing(0.6, 1),
  fontSize: '12px'
}));

export default function SkuDelist() {
  const appDispatch = useAppDispatch();

  // adding App Insights hook initialization for telemetry logging
  const { trackEvent, trackException } = useAppInsights();

  // moving moduleId, sourceId, scenarioId to top to avoid "used before defined" errors
  const { sourceId, moduleId, scenarioId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.DELIST
  });

  // moving currentScenarioId and navigate to top to avoid "used before defined" errors
  const { id: currentScenarioId } = useParams();
  const navigate = useNavigate();

  const {
    fetchingSimulationData,
    skuTableMetaData,
    fetchingTableData,
    enableSimulation,
    isScenarioSavedSuccessfully,
    isScenarioSaving,
    isSimulating,
    lastSimulatedSnapshot,
  } = useAppSelector((state) => state.skuDelist);
  const {
    globalFilterCombinations,
    globalFilterFormFields,
    globalFilterValue,
    globalFilterInitialValues
  } = useAppSelector((state) => state.filters);

  const { dynamicBreadcrumbs } = useAppSelector((state) => state.simulatorHome);
  const { simplification: delistAccessConfig } = useAppSelector(
    (state) => state.simulatorHome.moduleAccessMap
  );
  const { isFetchingPreRequisites } = useModulePreRequisites({
    module: MODULE_TO_PAGE_MAP.INNOVATION
  });
  const [isFilterValueChanged, setIsFilterValueChanged] = useState(false);

  const [isFollowPH, setFollowPH] = useState(false);

  const { isMenuOpen, anchorEl, handleMenuOpen, handleMenuClose } = useMenu();

  const [externalReset, setExternalReset] = useState(false);
  const formikRef = useRef();

  const { isDisplayComments } = useAppSelector((state) => state.drawerManager);

  const { tableMetadaData } = useAppSelector((state) => state.table);

  const { header } = tableMetadaData;

  const handleFollowPH = () => {
    const phHeaderMetadata = header?.filter(
      (ele) =>
        ele.parent.field_format === 'CHECKBOX' &&
        ele.parent.parent_field === 'delist_flag'
    )[0];

    const newValue = !isFollowPH;

    // adding telemetry log for PH recommendation toggle action
    trackEvent('Delist_PHRecommendationToggled', {
      previousValue: isFollowPH,
      newValue,
      triggeredFrom: 'menu'
    });

    setFollowPH(newValue);
    appDispatch(
      handlePhRecommendationCheckboxValueInputChange({
        columnMetadata: phHeaderMetadata,
        newValue
      })
    );

    handleMenuClose();
  };

  const handlePHInfo = () => {
    handleMenuClose();
  };

  const [columns, setColumns] = useState([]);

  const getSimulatedResult = () => {
    const idColumns: string[] = tableMetadaData.header
      .filter(
        (data) =>
          !data.parent.is_visible &&
          data.parent.field_format === InputType.NUMBER &&
          data.parent.alias.endsWith('_id')
      )
      .sort((a, b) => a.parent.level - b.parent.level)
      .map((data) => data.parent.parent_field);
    const simulatedData = getDataForSimulatedRows(
      tableMetadaData,
      idColumns,
      MODIFIED_ROW_INDICATOR_KEY.DELIST_IS_EDITED,
      MODIFIED_ROW_INDICATOR_KEY.DELIST_IS_NOT_DEFAULT,
      MODIFIED_ROW_INDICATOR_KEY.DELIST_IS_USER_INPUT
    );
    if (!simulatedData.inputs.length && !simulatedData.data.length) {
      return;
    }
    appDispatch(handleDelistSimulationFlagValue());
    const filters = transformDynamicFormValueToFilterPayload({
      formValue: globalFilterValue,
      formFields: globalFilterFormFields,
      combinations: globalFilterCombinations
    });
    // eslint-disable-next-line consistent-return
    return {
      filter_inputs: filters,
      row: simulatedData
    };
  };

  const handleNextFn = () => {
    OnNextClickHandler(
      dynamicBreadcrumbs,
      MODULE_TO_PAGE_MAP.DELIST,
      currentScenarioId,
      navigate
    );
  };

  const handleBreadcrumbClick = (routeURL: string): boolean => {
    if (routeURL === window.location.pathname) {
      return true;
    }
    if (!checkUnsavedChanges(true)) {
      return true;
    }
    setPendingNavigation({ type: 'breadcrumb', target: routeURL });
    setConfirmationDialogOpen(true);
    return false;
  };

  const onNextClick = () => {
    if (!checkUnsavedChanges(true)) {
      OnNextClickHandler(
        dynamicBreadcrumbs,
        MODULE_TO_PAGE_MAP.DELIST,
        currentScenarioId,
        navigate
      );
      return;
    }
    setPendingNavigation({ type: 'next' });
    setConfirmationDialogOpen(true);
  };

  const [isScenarioSuccessDialogOpen, setIsScenarioSuccessDialogOpen] =
    useState(false);

  const [scenarioSimulationSavingInfo, setScenarioSimulationSavingInfo] =
    useState({
      isDialogOpen: false,
      scenario: null,
      message: null
    });

  const [scenarioSimulationInfo, setScenarioSimulationInfo] = useState({
    isDialogOpen: false,
    scenario: null,
    message: null
  });

  const { callFunction } = useMatomoFunction();
  const getPageName = () => {
    const page_title = window.location.href.split('/').at(-1);
    return page_title;
  };

  const [isConfirmationDialogOpen, setConfirmationDialogOpen] = useState(false);

  const dialogMessage = "You have made changes on this page that haven't been simulated yet. Please click 'Simulate' to apply \nthem. If you proceed to the next module without simulating, these changes will be discarded. Are you \nsure you want to continue?"
  const dialogTitle = ""

  const [pendingNavigation, setPendingNavigation] = useState<{
    type: 'next' | 'breadcrumb';
    target?: string;
  } | null>(null);


  const checkUnsavedChanges = (compareAgainstSnapshot = false) => {
    const result = getChangedInputRows(compareAgainstSnapshot);
    return result.isChanged;
  };

  const handleConfirmationDialogClose = (result: DialogResult) => {
    if (result === DialogResult.YES && pendingNavigation) {
      if (pendingNavigation.type === 'next') {
        OnNextClickHandler(
          dynamicBreadcrumbs,
          MODULE_TO_PAGE_MAP.DELIST,
          currentScenarioId,
          navigate
        );
      } else if (pendingNavigation.type === 'breadcrumb' && pendingNavigation.target) {
        navigate(pendingNavigation.target);
      }
    }
    setConfirmationDialogOpen(false);
    setPendingNavigation(null);
  };

  useEffect(() => {
    callFunction(
      MatomoEventTrackMapObject.track_page_view.event_key,
      getPageName()
    );

    // adding telemetry log for page load with business context
    trackEvent('Delist_PageLoaded', {
      scenarioId: String(scenarioId || 'new'),
      moduleId: String(moduleId || ''),
      sourceId: String(sourceId || ''),
      hasSavedScenario: !!currentScenarioId,
      accessLevel: delistAccessConfig?.save ? 'full' : 'read-only'
    });
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (isSimulating) {
        setScenarioSimulationInfo({
          isDialogOpen: true,
          scenario: null,
          message: 'Simulating'
        });
      } else {
        setScenarioSimulationInfo({
          ...scenarioSimulationInfo,
          isDialogOpen: false
        });
      }
    }, 500);
  }, [isSimulating]);

  useEffect(() => {
    setTimeout(() => {
      if (isScenarioSavedSuccessfully) {
        setScenarioSimulationSavingInfo({
          ...scenarioSimulationSavingInfo,
          isDialogOpen: false
        });
        setIsScenarioSuccessDialogOpen(true);
        appDispatch(handleValueOfDelistSavedScenario());
      }
    }, 500);
  }, [isScenarioSavedSuccessfully]);

  useEffect(() => {
    setTimeout(() => {
      if (isScenarioSaving) {
        setScenarioSimulationSavingInfo({
          isDialogOpen: true,
          scenario: null,
          message: 'Saving'
        });
      }
    }, 500);
  }, [isScenarioSaving]);

  const handleScenarioCreateSuccessDialogClose = () => {
    setIsScenarioSuccessDialogOpen(false);
  };

  const getColumnsData = (tableResData: any) => {
    const { inputCols, resultCols, response } = tableResData;

    appDispatch(setTableMetaData({ tableData: response }));
    setColumns([
      ...getTableColumnData(
        inputCols,
        TableComponent.DELIST,
        response.header,
        [],
        !delistAccessConfig.provide_inputs,
        moduleId,
        sourceId
      ),
      ...getOutputTableColumn(
        resultCols,
        response.output_toggle,
        response.footer.row,
        {},
        TableComponent.DELIST
      )
    ]);
  };

  const handleFilterApply = (filters: IFilterObj[]) => {
    appDispatch(
      getSKUDelistData({
        dataInput: {
          filter_inputs: filters,
          row: []
        },
        sourceId,
        moduleId,
        scenarioId
      })
    ).then((res: any) => {
      getColumnsData(res.payload);
    });
  };

  const getChangedInputRows = (compareAgainstSnapshot = false) => {
    if (compareAgainstSnapshot && lastSimulatedSnapshot) {
      const simulatedData = getDataForSimulatedRowsWithBaseline(
        tableMetadaData,
        lastSimulatedSnapshot
      );
      return {
        isChanged: simulatedData.inputs.length > 0,
        row: simulatedData
      };
    }
    const idColumns: string[] = tableMetadaData.header
      .filter(
        (data) =>
          !data.parent.is_visible &&
          data.parent.field_format === InputType.NUMBER &&
          data.parent.alias.endsWith('_id')
      )
      .sort((a, b) => a.parent.level - b.parent.level)
      .map((data) => data.parent.parent_field);
    const simulatedData = getDataForSimulatedRows(
      tableMetadaData,
      idColumns,
      MODIFIED_ROW_INDICATOR_KEY.DELIST_IS_EDITED,
      MODIFIED_ROW_INDICATOR_KEY.DELIST_IS_NOT_DEFAULT,
      MODIFIED_ROW_INDICATOR_KEY.DELIST_IS_USER_INPUT
    );
    return {
      isChanged: simulatedData.inputs.length > 0 || simulatedData.data.length > 0,
      row: simulatedData
    };
  };

  const handleSimulationClick = () => {
    // adding telemetry log for simulation initiation with context
    trackEvent('Delist_SimulationInitiated', {
      hasFilters: Object.keys(globalFilterValue || {}).length > 0,
      isFollowPH,
      scenarioId: String(scenarioId || 'new'),
      sourcePage: window.location.pathname
    });

    callFunction(
      MatomoEventTrackMapObject.track_simulate_button_click.event_key,
      getPageName()
    );
    const result = getChangedInputRows();
    if (!result.isChanged) {
      // adding telemetry log for skipped simulation due to no changes
      trackEvent('Delist_SimulationSkipped', {
        reason: 'no_changes_detected'
      });
      return;
    }
    const dataPoints = getSimulatedResult();
    appDispatch(
      getSKUDelistData({
        dataInput: dataPoints,
        sourceId,
        moduleId,
        scenarioId
      })
    ).then((res: any) => {
      // adding telemetry log for successful simulation completion
      // fixing "Property 'tableData' does not exist on type 'unknown'" with type assertion
      const payload = res.payload as { tableData?: { row?: any[] } };
      trackEvent('Delist_SimulationCompleted', {
        rowCount: payload?.tableData?.row?.length || 0,
        duration: Date.now()
      });
      getColumnsData(res.payload);
    }).catch((err) => {
      // adding telemetry log for simulation error with context
      trackException(err, {
        context: 'Delist_Simulation',
        scenarioId: String(scenarioId || 'new')
      });
    });
  };

  const handleClickOpen = () => {
    // adding telemetry log for scenario save initiation
    trackEvent('Delist_ScenarioSaveInitiated', {
      scenarioId: String(scenarioId || 'new'),
      hasSimulated: !!lastSimulatedSnapshot,
      filterCount: Object.keys(globalFilterValue || {}).length
    });

    appDispatch(
      saveDelistSimulationData({
        sourceId,
        moduleId,
        scenarioId
      })
    );
  };

  const handleSimulationReset = () => {
    if (formikRef.current) {
      (formikRef.current as any).resetForm();
    }
    setExternalReset(true);
    setTimeout(() => {
      setExternalReset(false);
    }, 2000);
    appDispatch(setGlobalFilterValues(globalFilterInitialValues));
    appDispatch(
      setGlobalFilterFieldOptions(
        getDefaultOptionsForFormFields(
          globalFilterFormFields,
          globalFilterCombinations,
          globalFilterInitialValues
        )
      )
    );
    // TODO: resetting the state of table columns after reset simulation
    appDispatch(
      resetDelistSimulationData({
        sourceId,
        moduleId,
        scenarioId
      })
    ).then((res: any) => {
      getColumnsData(res.payload);
      // resetting the state of ph followed
      setFollowPH(false);
    });
  };

  if (isFetchingPreRequisites) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      </Grid>
    );
  }
  return (
    <>
      <Grid sx={{ padding: (theme) => theme.spacing(1, 2) }}>
        <FiltersWrapper
          accessConfig={delistAccessConfig}
          externalReset={externalReset}
          moduleId={moduleId}
          onFilterFieldsFetched={handleFilterApply}
          onValueApply={handleFilterApply}
          scenarioId={scenarioId}
          sourceId={sourceId}
          setIsFilterValueChanged={(value) => {
            setIsFilterValueChanged(value);
          }}
        />
        <Grid
          container
          spacing={1}
          alignItems='center'
          justifyContent='space-between'
        >
          <Grid item md={7}>
            <DelistSkuInfo />
          </Grid>
          {/* eslint-disable arrow-body-style  */}
          {skuTableMetaData.legend.metadata &&
            skuTableMetaData.legend.metadata.length > 0 &&
            skuTableMetaData.legend.metadata.map((legendData) => {
              return (
                <Grid item md={2} key={legendData.label}>
                  <div className='sku-legend-div' style={{ margin: 'auto' }}>
                    <div
                      className='sku-legend-box'
                      style={{
                        background: `${legendData.color}`,
                        marginTop: '4px'
                      }}
                    />
                    <div className='legend-title' style={{ marginTop: '7px' }}>
                      {legendData.label}{' '}
                    </div>
                  </div>
                </Grid>
              );
            })}
          <Grid
            justifyContent='flex-end'
            alignContent='end'
            spacing={1}
            item
            xs='auto'
          >
            <Button
              sx={{ ml: 1, mr: 1 }}
              variant='outlined'
              onClick={handleMenuOpen}
            >
              PH Tool Options
            </Button>
            <Menu
              id='basic-menu'
              anchorEl={anchorEl}
              open={isMenuOpen}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right'
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left'
              }}
              MenuListProps={{
                'aria-labelledby': 'basic-button'
              }}
              sx={{ padding: '4px 0 4px 0' }}
            >
              <StyledMenuItem onClick={handleFollowPH}>
                <img src='/static/images/ph-follow-icon.svg' alt='' />
                <div className='ph-menu-item'>
                  {!isFollowPH ? 'Follow PH Delist' : 'Unfollow PH Delist'}
                </div>
              </StyledMenuItem>
              <StyledMenuItem onClick={handlePHInfo}>
                <img src='/static/images/ph-info-icon.svg' alt='' />
                <div className='ph-menu-item'>About PH Tool</div>
              </StyledMenuItem>
            </Menu>
            <SaveAsContainer disabled={!delistAccessConfig?.save_as} />

            <Button
              sx={{ ml: 1 }}
              variant='contained'
              onClick={handleClickOpen}
              disabled={!enableSimulation || !delistAccessConfig?.save}
            >
              Save
            </Button>
          </Grid>
        </Grid>
        <Box style={{ marginTop: '10px' }}>
          <Grid container>
            {(fetchingTableData || !columns.length) && (
              <Grid item xs={12}>
                <TableSkeletonContainer rowLength={10} headerLength={6} />
              </Grid>
            )}
            {!fetchingTableData && columns.length > 0 && (
              <Grid item xs={12}>
                <DelistTableComponent columns={columns} />
              </Grid>
            )}
          </Grid>
        </Box>
        {isScenarioSuccessDialogOpen ? (
          <ScenarioCreateSuccessDialog
            imagePath='/static/images/success.svg'
            isOpen={isScenarioSuccessDialogOpen}
            onClose={handleScenarioCreateSuccessDialogClose}
            message='Scenario has been successfully Saved'
          />
        ) : null}

        <ScenarioCreateSuccessDialog
          isOpen={scenarioSimulationInfo.isDialogOpen}
          imagePath='/static/images/file-text.svg'
          message={scenarioSimulationInfo.message}
        />
        <ScenarioCreateSuccessDialog
          isOpen={scenarioSimulationSavingInfo.isDialogOpen}
          imagePath='/static/images/upload-cloud.svg'
          message={scenarioSimulationSavingInfo.message}
        />
      </Grid>
      {isDisplayComments ? <DrawerBoxContainer /> : null}
      <SimulatorFooter
        simulationClickHandler={handleSimulationClick}
        resetSimulationHandler={handleSimulationReset}
        isSimulationDisabled={
          fetchingSimulationData ||
          !delistAccessConfig.simulate ||
          isFilterValueChanged
        }
        isResetSimulationDisabled={
          fetchingSimulationData || !delistAccessConfig.reset_simulation
        }
        isNextDisabled={fetchingSimulationData}
        nextClickHandler={onNextClick}
        isButtonSectionVisible
        onBreadcrumbClick={handleBreadcrumbClick}
      />
      <AlertDialog
        closeHandler={handleConfirmationDialogClose}
        isDialogOpen={isConfirmationDialogOpen}
        message={dialogMessage}
        title={dialogTitle}
      />
    </>
  );
}